import { useCallback, useEffect, useMemo } from "react";
import { AdminService } from "../../../../../services";
import { getColumns, getExpandTableColumns } from "../../config";
import { useApplications } from "../../../../../context/Applications";

const isBlocked = (val) => ['inactive', 'merged'].includes(val);

export const useBlocked = ({
  blockedFetched = false,
  search = '',
  activeFilters = {},
  perPage = 20,
  dataSource = [],
  setLoading = () => { },
  fetchUsers = () => { },
  setPage = () => { },
  fetchBlocked = () => { },
  columnConfig = [],
  options = {},
  t = (s) => s,
  userRoles = [],
}) => {
  const { applications } = useApplications();

  useEffect(() => {
    if (blockedFetched) {
      fetchBlocked({ search, activeFilters, page: 1, perPage });
      setPage(1);
    }
  }, [search, activeFilters]);

  const _dataSource = useMemo(() => {
    return dataSource
      .filter((v) => isBlocked(v.status) || v.users.find((u) => isBlocked(u.status)))
      .map((c) => ({
        ...c,
        companyDisabled: isBlocked(c.status),
        users: isBlocked(c.status) ? [
          ...(c.users || []).map((u) => ({ ...u, companyDisabled: true })),
          ...(c.pendingUsers || []).map((u, i) => ({ ...u, status: 'pending', id: u.invitationToken }))
        ] : (c.users || []).filter((u) => isBlocked(u.status)),
      }));
  }, [dataSource]);

  useEffect(() => {
    if (!blockedFetched) {
      fetchBlocked({});
    }
  }, [blockedFetched]);

  const onReactivate = useCallback(async (u) => {
    setLoading(true);

    try {
      await AdminService.disableCompany(u.id);
      fetchBlocked({});
      fetchUsers({});
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading, fetchUsers, fetchBlocked]);

  const onReactivateUser = useCallback(async (id) => {
    try {
      await AdminService.activateUser(id);
      fetchUsers({});
      fetchBlocked({});
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading, fetchUsers, fetchBlocked]);

  const onResendVerification = useCallback(async (id) => {
    setLoading(true);

    try {
      await AdminService.resendVerification(id);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  }, [setLoading]);

  const headerColumns = useMemo(() =>
    getColumns({ t, ...columnConfig, options, view: 'blocked', onReactivate, onResendVerification, applications })
      .filter((v) => v.show), [t, columnConfig, onReactivate, onResendVerification, options, applications]);

  const expandedTableColumns = useMemo(() =>
    getExpandTableColumns({ t, ...columnConfig, view: 'blocked', reactivateUser: onReactivateUser, onResendVerification, userRoles, applications })
      .filter((v) => v.show), [t, columnConfig, onReactivateUser, userRoles, onResendVerification, applications]);

  return {
    _dataSource,
    headerColumns,
    expandedTableColumns,
  }
}
